import React, { useEffect, useState, useRef } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography, Box } from "@mui/material";
import { IniInfo } from "system/types/interfaces";
import { ApplicabilityRuleBuilder } from "../../subjectViewPage/applicabilityTab/ApplicabilityRuleBuilder";
import { useDebouncedCallback } from "use-debounce";

interface ActiveDirectoryDomainModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: IniInfo) => void;
  initialDomain: IniInfo | null;
  initialComment: IniInfo | null;
  mode: "add" | "edit";
}

const ActiveDirectoryDomainModal: React.FC<ActiveDirectoryDomainModalProps> = ({
  open,
  onClose,
  onSubmit,
  initialDomain,
  initialComment,
  mode,
}) => {
  // Freeze the mode while the modal is open so the parent clearing initialComment
  // during close does not flash the Rule mode during the Dialog fade-out.
  const [isCommentMode, setIsCommentMode] = useState(false);

  useEffect(() => {
    if (open) setIsCommentMode(initialComment !== null);
  }, [open, initialComment]);

  const [localDomain, setLocalDomain] = useState<IniInfo>({
    name: "",
    value: "",
    delimiter: "is for any",
    inlineComment: "",
  });
  const [nameError, setNameError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState<boolean>(true);
  const [commentText, setCommentText] = useState<string>("");
  const nameRef = useRef<HTMLDivElement>(null);
  const commentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isCommentMode && initialComment) {
      setCommentText((initialComment.value || "").replace(/^;\s*/, ""));
    } else if (initialDomain) {
      setLocalDomain(initialDomain);
    } else {
      setLocalDomain({ name: "", value: "", delimiter: "is for any", inlineComment: "" });
      setCommentText("");
    }
  }, [initialDomain, initialComment, isCommentMode, open]);

  useEffect(() => {
    if (!open) return;
    if (isCommentMode) commentRef.current?.querySelector("textarea")?.focus();
    else nameRef.current?.querySelector("input")?.focus();
  }, [open, isCommentMode]);

  const validateDomainName = (name: string): string | null => {
    if (name === '""' || name.trim() === "") return null;

    const trimmed = name.trim();

    if (trimmed.startsWith(".")) {
      return "Domain must not start with a period (.)";
    }

    const allowedRegex = /^[a-zA-Z0-9][a-zA-Z0-9.\-]*[a-zA-Z0-9]$/;

    if (!allowedRegex.test(trimmed)) {
      return "Invalid character. Only letters, numbers, dots (.), and hyphens (-) are allowed.";
    }

    return null;
  };

  const debouncedValidate = useDebouncedCallback((value: string) => {
    const err = validateDomainName(value);
    setNameError(err);
  }, 500);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.trim();
    if (val === "") val = '""';
    setLocalDomain((prev) => ({ ...prev, name: val }));
    debouncedValidate(val);
  };

  const handleBuilderChange = (result: {
    finalValue: string;
    finalDelimiter: string;
    comment: string;
    fullSyntax: string;
    isValid: boolean;
  }) => {
    if (!result.isValid) {
      setIsValid(result.isValid);
      return;
    }
    setIsValid(result.isValid);
    if (
      localDomain.value === result.finalValue &&
      localDomain.delimiter === result.finalDelimiter &&
      localDomain.inlineComment === result.comment
    ) {
      return;
    }

    setLocalDomain((prev) => ({
      ...prev,
      value: result.finalValue,
      delimiter: result.finalDelimiter,
      inlineComment: result.comment,
    }));
  };

  const handleSubmit = () => {
    if (isCommentMode && initialComment) {
      const trimmed = commentText.trim();
      if (!trimmed) return;
      const finalValue = trimmed.startsWith(";") ? trimmed : `; ${trimmed}`;
      onSubmit({
        ...initialComment,
        value: finalValue,
        delimiter: "",
        inlineComment: "",
        isComment: true,
      });
      onClose();
      return;
    }

    if (nameError) return;
    onSubmit(localDomain);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ backgroundColor: "#1b1a19", color: "#fff" }}>
        <Typography variant="h6" fontWeight="bold">
          {isCommentMode ? "Edit Comment" : mode === "add" ? "Add Domain" : "Edit Domain"}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: "#1b1a19", pt: 2 }}>
        <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 3 }}>
          {isCommentMode ? (
            <TextField
              fullWidth
              label="Comment"
              multiline
              rows={7}
              placeholder="Add your comment here"
              value={commentText}
              inputProps={{ style: { color: "#4caf50" } }}
              ref={commentRef}
              onChange={(e) => setCommentText(e.target.value)}
            />
          ) : (
            <>
              <TextField
                label="Domain Name"
                placeholder='e.g. internal.meetaiden.com or ""'
                value={localDomain.name === '""' ? "" : localDomain.name}
                onChange={handleNameChange}
                FormHelperTextProps={{ sx: { color: nameError ? "#d32f2f" : "#aaa" } }}
                ref={nameRef}
                fullWidth
                error={!!nameError}
                helperText={nameError || "leave blank to skip the domain join"}
              />
              <ApplicabilityRuleBuilder
                initialName={localDomain.name === '""' ? "" : localDomain.name || ""}
                initialDelimiter={localDomain.delimiter}
                initialValue={localDomain.value}
                initialComment={localDomain.inlineComment || ""}
                isUpdating={true}
                onChange={handleBuilderChange}
                shouldDisplayNameValue={true}
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#1b1a19", p: 2 }}>
        <Button variant="outlined" onClick={onClose} sx={{ color: "#fff", borderColor: "#757575" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isCommentMode ? !commentText.trim() : !!nameError || !isValid}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { ActiveDirectoryDomainModal };
